/**
 * Live reads of `aiChats` for the building assistant panel. The only real-time listeners in
 * the app: the answer is produced server-side and arrives through the chat node (`pending`,
 * then `turns`), so the panel has to follow it as it changes.
 */
import { useEffect, useState } from 'react';
import { equalTo, getDatabase, onValue, orderByChild, query, ref } from 'firebase/database';
import { AI_CHATS_ROOT } from '../../api/assistantApi';
import { chatListItems, type ChatListItem } from '../../Helpers/assistantChatView';
import type { AssistantChat } from '../../types/assistant';

export type LiveState<T> = { status: 'loading' } | { status: 'error' } | { status: 'loaded'; value: T };

/** The owner's chats, newest first. Not read while `uid` is undefined. */
export function useAssistantChatList(uid: string | undefined): LiveState<ChatListItem[]> {
    const [state, setState] = useState<LiveState<ChatListItem[]>>({ status: 'loading' });

    useEffect(() => {
        if (!uid) return;
        setState({ status: 'loading' });
        const chats = query(ref(getDatabase(), AI_CHATS_ROOT), orderByChild('ownerUid'), equalTo(uid));
        return onValue(
            chats,
            (snapshot) => setState({ status: 'loaded', value: chatListItems(snapshot.val()) }),
            (error) => {
                console.error('Error loading assistant chats:', error);
                setState({ status: 'error' });
            }
        );
    }, [uid]);

    return state;
}

/**
 * One chat, live. `null` until the first question has created it (the key is generated
 * client-side before `askAssistant` runs). Not read while `chatKey` is undefined.
 */
export function useAssistantChat(chatKey: string | undefined): LiveState<AssistantChat | null> {
    const [state, setState] = useState<LiveState<AssistantChat | null>>({ status: 'loading' });

    useEffect(() => {
        if (!chatKey) return;
        setState({ status: 'loading' });
        return onValue(
            ref(getDatabase(), `${AI_CHATS_ROOT}/${chatKey}`),
            (snapshot) => setState({ status: 'loaded', value: (snapshot.val() as AssistantChat | null) ?? null }),
            (error) => {
                console.error('Error loading assistant chat:', error);
                setState({ status: 'error' });
            }
        );
    }, [chatKey]);

    return state;
}

/** A clock that ticks while `active`, so a stale `pending` turns into "failed" without new data. */
export function useNow(active: boolean, intervalMs = 15_000): Date {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        if (!active) return;
        setNow(new Date());
        const id = setInterval(() => setNow(new Date()), intervalMs);
        return () => clearInterval(id);
    }, [active, intervalMs]);
    return now;
}
